import { Check, X, Spinner } from "@phosphor-icons/react";
import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";

interface TicketInlineEditProps {
  ticketId: string | undefined;
  ticketTitle: string;
  onSave: (title: string) => Promise<void>;
  onCancel: () => void;
}

export function TicketInlineEdit({
  ticketId,
  ticketTitle,
  onSave,
  onCancel,
}: TicketInlineEditProps) {
  const { t } = useTranslation("ui");
  const [editValue, setEditValue] = useState(ticketTitle);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  // why: blur can run before this render's isSubmitting is true, and cancelling then unmounts the form so the rename never saves.
  const submittingRef = useRef(false);
  const blurCancelTimer = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (blurCancelTimer.current != null) {
        window.clearTimeout(blurCancelTimer.current);
      }
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    submittingRef.current = true;
    if (!editValue.trim()) {
      submittingRef.current = false;
      return;
    }
    setIsSubmitting(true);
    try {
      await onSave(editValue);
      // why: the ref must mean "a save is in flight", not "this instance ever
      // submitted" — clear it before onCancel so blur-cancel still works if the
      // caller keeps this form mounted across the save.
      submittingRef.current = false;
      // Close the edit form on successful save
      onCancel();
    } catch (err) {
      console.error("Failed to rename ticket:", err);
      // why: a blur may have armed the 0ms cancel before this submit. The
      // rejection settles as a microtask, so that timer is still pending and
      // would re-read the ref below as false and close the editor, dropping
      // the typed title. Clear it before restoring the retry state.
      if (blurCancelTimer.current != null) {
        window.clearTimeout(blurCancelTimer.current);
        blurCancelTimer.current = null;
      }
      // Keep editing state active so user can retry
      submittingRef.current = false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      onCancel();
    }
    if (e.key === "Enter" && !e.shiftKey && !isSubmitting && editValue.trim()) {
      e.preventDefault();
      void handleSubmit(e as unknown as React.FormEvent);
    }
  };

  const handleBlur = (e: React.FocusEvent) => {
    // Check if the new focus target is within the same form
    const relatedTarget = e.relatedTarget as HTMLElement;
    const formElement = e.currentTarget.closest("form");

    // Don't close if the new focus target is within the same form
    if (relatedTarget && formElement && formElement.contains(relatedTarget)) {
      return;
    }

    if (isSubmitting || submittingRef.current) {
      return;
    }

    // why: Enter and requestSubmit blur with relatedTarget null before submit; cancelling in that turn drops the rename.
    if (blurCancelTimer.current != null) {
      window.clearTimeout(blurCancelTimer.current);
    }
    blurCancelTimer.current = window.setTimeout(() => {
      blurCancelTimer.current = null;
      if (!submittingRef.current) {
        onCancel();
      }
    }, 0);
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="flex min-w-0 items-center"
      data-testid={`breadcrumb-ticket-edit-form-${ticketId || "unknown"}`}
    >
      <input
        ref={inputRef}
        className="bg-white font-inherit m-0 w-full min-w-0 rounded border border-puntt-blue-9 p-1 text-sm font-medium leading-5 shadow-sm outline-none focus:ring-0"
        value={editValue}
        autoFocus
        onFocus={(e) => e.target.select()}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        data-testid={`breadcrumb-ticket-edit-input-${ticketId || "unknown"}`}
        disabled={isSubmitting}
      />
      <button
        type="submit"
        className="hover:bg-green-50 ml-1 flex cursor-pointer items-center justify-center rounded border-none bg-transparent p-1 disabled:cursor-not-allowed disabled:opacity-50"
        aria-label={t("tickets.ticketInlineEdit.saveTicketName")}
        tabIndex={-1}
        disabled={!editValue.trim() || isSubmitting}
        onMouseDown={(e) => {
          // Prevent the input from losing focus when clicking the submit button
          // Only prevent default if the button is enabled (not disabled)
          const isDisabled = !editValue.trim() || isSubmitting;
          if (!isDisabled) {
            e.preventDefault();
          }
        }}
        data-testid={`breadcrumb-ticket-save-button-${ticketId || "unknown"}`}
      >
        {isSubmitting ? (
          <Spinner size="1" />
        ) : (
          <Check size={18} className="text-green-600" />
        )}
      </button>
      <button
        type="button"
        className="hover:bg-red-50 ml-1 flex cursor-pointer items-center justify-center rounded border-none bg-transparent p-1 disabled:cursor-not-allowed disabled:opacity-50"
        aria-label={t("tickets.ticketInlineEdit.cancelTicketNameEdit")}
        onClick={onCancel}
        tabIndex={-1}
        disabled={isSubmitting}
        data-testid={`breadcrumb-ticket-cancel-button-${ticketId || "unknown"}`}
      >
        <X size={18} className="text-red-600" />
      </button>
    </form>
  );
}
